import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import { showToast } from './Toast';
import { showConfirm } from './ConfirmDialog';
import { NEUTRAL, STATUS_COLORS, resolveTheme, onAccent } from './designTokens';
import Card from './components/primitives/Card';
import SectionCard from './components/primitives/SectionCard';
import Tabs from './components/primitives/Tabs';
import Button from './components/primitives/Button';
import Badge from './components/primitives/Badge';
import Avatar from './components/primitives/Avatar';
import EmptyState from './components/primitives/EmptyState';
import ListItem from './components/primitives/ListItem';
import FormInput from './components/primitives/FormInput';
import { KLINIO_DEFAULT_ACCENT } from './BrandContext';
import { currencyLabel } from './clientUtils';

function ClientProfile({ therapist, client, onBack, onNewBooking, onDeleteClient, accentColor = KLINIO_DEFAULT_ACCENT }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [programs, setPrograms] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(client.name);
  const [email, setEmail] = useState(client.email || '');
  const [phone, setPhone] = useState(client.phone || '');
  const [compliance, setCompliance] = useState(null);
  const [loadingCompliance, setLoadingCompliance] = useState(false);
  const [anamnesisResponse, setAnamnesisResponse] = useState(null);
  const [anamnesisTemplate, setAnamnesisTemplate] = useState(null);
  const [anamnesisQuestions, setAnamnesisQuestions] = useState([]);
  const [anamnesisLoading, setAnamnesisLoading] = useState(false);
  const [manualFillMode, setManualFillMode] = useState(false);
  const [manualAnswers, setManualAnswers] = useState({});
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [therapistNote, setTherapistNote] = useState('');
  const [therapistNoteUpdatedAt, setTherapistNoteUpdatedAt] = useState(null);
  const [editingNote, setEditingNote] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [editAnamnesisMode, setEditAnamnesisMode] = useState(false);
  const [editAnswers, setEditAnswers] = useState({});
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [complianceError, setComplianceError] = useState(null);
  const [anamnesisError, setAnamnesisError] = useState(null);
  const [anamnesisLoaded, setAnamnesisLoaded] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedProgramSource, setSelectedProgramSource] = useState(null);

  // Programtilldelning direkt från kundprofil
  const [allPrograms, setAllPrograms] = useState([]);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [assignProgramId, setAssignProgramId] = useState('');
  const [assignStartDate, setAssignStartDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [assignEndDate, setAssignEndDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 56); return d.toISOString().split('T')[0];
  });
  const [assignFrequency, setAssignFrequency] = useState('3');
  const [assigning, setAssigning] = useState(false);

  const [clientBookings, setClientBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [latestMessage, setLatestMessage] = useState(null);

  useEffect(() => {
    fetchCompliance();
    fetchAnamnesis();
    fetchClientBookings();
    fetchMessageSummary();
  }, []);

  // Olästa + senaste meddelande (för fliksiffra och översiktens utdrag)
  const fetchMessageSummary = async () => {
    const [{ count }, { data: latest }] = await Promise.all([
      supabase.from('messages').select('id', { count: 'exact', head: true })
        .eq('therapist_id', therapist.id).eq('client_id', client.id).eq('sender_role', 'client').eq('read', false),
      supabase.from('messages').select('content, sender_role, created_at')
        .eq('therapist_id', therapist.id).eq('client_id', client.id)
        .order('created_at', { ascending: false }).limit(1).maybeSingle(),
    ]);
    setUnreadCount(count || 0);
    setLatestMessage(latest || null);
  };

  const fetchCompliance = async () => {
    setLoadingCompliance(true);
    setComplianceError(null);
    try {
      // Hämta kundens program med block och övningar
      const { data: cpData, error: cpError } = await supabase
        .from('client_programs')
        .select('*, program:programs(*, program_blocks(*, program_exercises(*, exercise:exercises(*))))')
        .eq('client_id', client.id);
      if (cpError) throw cpError;

      // Hämta alla loggar
      const { data: logsData, error: logsError } = await supabase
        .from('exercise_logs')
        .select('*')
        .eq('client_id', client.id)
        .order('completed_at', { ascending: false });
      if (logsError) throw logsError;

      const logs = logsData || [];
      const programsList = cpData || [];

      // Beräkna statistik per program
      const programStats = programsList.map(cp => {
        const program = cp.program;
        if (!program) return null;

        const blocks = program.program_blocks || [];
        const programLogs = logs.filter(l => l.program_id === program.id);

        // Unika dagar med logg
        const uniqueDays = [...new Set(programLogs.map(l => l.completed_at?.split('T')[0]))].filter(Boolean);

        // Aktuell vecka och aktivt block – beräknas före 7-dagarsvyn så att rätt total används
        const now = new Date();
        const currentWeek = cp.start_date ? Math.max(1, Math.floor((now - new Date(cp.start_date)) / (1000 * 60 * 60 * 24 * 7)) + 1) : null;
        const totalWeeks = cp.start_date && cp.end_date ? Math.max(1, Math.ceil((new Date(cp.end_date) - new Date(cp.start_date)) / (1000 * 60 * 60 * 24 * 7))) : null;
        const activeBlock = blocks.find(b => currentWeek && currentWeek >= b.start_week && currentWeek <= b.end_week);

        // Antal övningar i aktivt block används som referensvärde för 7-dagarsvyn.
        // Fallback: alla övningar i programmet (om inget block matchar aktuell vecka).
        const allExerciseIds = blocks.flatMap(b => (b.program_exercises || []).map(pe => pe.exercise_id));
        const activeExerciseCount = activeBlock
          ? (activeBlock.program_exercises || []).length
          : allExerciseIds.length;

        // Senaste 7 dagarna
        const last7 = [];
        for (let i = 6; i >= 0; i--) {
          const d = new Date(now);
          d.setDate(d.getDate() - i);
          const dateStr = d.toISOString().split('T')[0];
          const dayLogs = programLogs.filter(l => l.completed_at?.startsWith(dateStr));
          const dayName = d.toLocaleDateString('sv-SE', { weekday: 'short' });
          last7.push({ date: dateStr, dayName, count: dayLogs.length, total: activeExerciseCount });
        }

        // Mest hoppade övningar (övningar utan logg senaste 7 dagarna)
        const activeExercises = activeBlock ? (activeBlock.program_exercises || []) : [];
        const skippedExercises = activeExercises
          .map(pe => {
            const exLogs = last7.reduce((sum, day) => {
              return sum + programLogs.filter(l => l.completed_at?.startsWith(day.date) && l.exercise_id === pe.exercise_id).length;
            }, 0);
            return { title: pe.exercise?.title, doneCount: exLogs, totalDays: 7 };
          })
          .sort((a, b) => a.doneCount - b.doneCount);

        return {
          title: program.title,
          programId: program.id,
          cpRef: cp,
          startDate: cp.start_date,
          endDate: cp.end_date,
          frequency: cp.frequency_per_week,
          currentWeek,
          totalWeeks,
          activeBlock: activeBlock?.title || null,
          totalLogs: programLogs.length,
          uniqueDays: uniqueDays.length,
          last7,
          skippedExercises,
        };
      }).filter(Boolean);

      setCompliance({ programStats, totalLogs: logs.length });
    } catch (err) {
      console.error('[fetchCompliance]', err);
      setComplianceError('Kunde inte ladda aktivitetsdata.');
    } finally {
      setLoadingCompliance(false);
    }
  };

  const fetchPrograms = async () => {
    setLoadingPrograms(true);
    try {
      const [{ data: cpData, error: cpError }, { data: progData, error: progError }] = await Promise.all([
        supabase
          .from('client_programs')
          .select('*, program:programs(*, program_blocks(*, program_exercises(*, exercise:exercises(*))))')
          .eq('client_id', client.id),
        supabase
          .from('programs')
          .select('id, title, description')
          .eq('therapist_id', therapist.id)
          .order('created_at', { ascending: false }),
      ]);
      if (cpError) throw cpError;
      if (progError) throw progError;
      setPrograms(cpData || []);
      setAllPrograms(progData || []);
    } catch (err) {
      console.error('[fetchPrograms]', err);
    } finally {
      setLoadingPrograms(false);
    }
  };

  const handleAssign = async () => {
    if (!assignProgramId) return;
    setAssigning(true);
    const { data: inserted, error } = await supabase.from('client_programs').insert({
      client_id: client.id,
      program_id: assignProgramId,
      start_date: assignStartDate,
      end_date: assignEndDate || null,
      frequency_per_week: Number(assignFrequency),
      client_seen: false,
    }).select('id').single();
    setAssigning(false);
    if (error) { showToast('Fel: ' + error.message, 'error'); return; }
    showToast('Program tilldelat', 'success');
    setShowAssignForm(false);
    setAssignProgramId('');
    fetchPrograms();
    fetchCompliance();
    // Skicka programnotis till kunden (tyst om kunden saknar e-post)
    if (inserted?.id) {
      supabase.functions.invoke('send-program-notification', {
        body: { client_program_id: inserted.id },
      }).catch(() => {}); // fire and forget
    }
  };

  const endAssignment = async (cpId) => {
    const todayStr = new Date().toISOString().split('T')[0];
    const { error } = await supabase
      .from('client_programs')
      .update({ end_date: todayStr })
      .eq('id', cpId);
    if (error) {
      console.error('[endAssignment]', error);
      showToast('Fel: ' + error.message, 'error');
    } else {
      showToast('Program avslutat', 'success');
      fetchPrograms();
      fetchCompliance();
    }
  };

  const unassignClient = (cpId) => {
    showConfirm({
      title: 'Ta bort tilldelning',
      description: 'Kunden tas bort från programmet.',
      onConfirm: async () => {
        const { error } = await supabase.from('client_programs').delete().eq('id', cpId);
        if (error) { showToast('Fel: ' + error.message, 'error'); return; }
        fetchPrograms();
        fetchCompliance();
      },
    });
  };

  const fetchAnamnesis = async () => {
    setAnamnesisLoading(true);
    setAnamnesisError(null);
    try {
      const { data: tmpl, error: tmplError } = await supabase
        .from('anamnesis_templates')
        .select('*')
        .eq('therapist_id', therapist.id)
        .maybeSingle();
      if (tmplError) throw tmplError;
      setAnamnesisTemplate(tmpl || null);

      if (tmpl) {
        const { data: questions, error: qError } = await supabase
          .from('anamnesis_questions')
          .select('*')
          .eq('template_id', tmpl.id)
          .order('sort_order');
        if (qError) throw qError;
        setAnamnesisQuestions(questions || []);

        const { data: resp, error: respError } = await supabase
          .from('anamnesis_responses')
          .select('*, answers:anamnesis_answers(*, question:anamnesis_questions(*))')
          .eq('therapist_id', therapist.id)
          .eq('client_id', client.id)
          .maybeSingle();
        if (respError) throw respError;
        setAnamnesisResponse(resp || null);
        setTherapistNote(resp?.therapist_notes || '');
        setTherapistNoteUpdatedAt(resp?.therapist_notes_updated_at || null);
      } else {
        setAnamnesisQuestions([]);
        setAnamnesisResponse(null);
        setTherapistNote('');
        setTherapistNoteUpdatedAt(null);
      }
    } catch (err) {
      console.error('[fetchAnamnesis]', err);
      setAnamnesisError('Kunde inte ladda anamnesdata.');
    } finally {
      setAnamnesisLoading(false);
      setAnamnesisLoaded(true);
    }
  };

  const fetchClientBookings = async () => {
    setLoadingBookings(true);
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select('id, date, start_time, end_time, status, service:services(title), price_snapshot, currency_snapshot, campaign_price_snapshot, campaign_active_snapshot')
        .eq('therapist_id', therapist.id)
        .eq('client_id', client.id)
        .order('date', { ascending: true })
        .order('start_time', { ascending: true });
      if (error) throw error;
      setClientBookings(data || []);
    } catch (err) {
      console.error('[fetchClientBookings]', err);
    } finally {
      setLoadingBookings(false);
    }
  };

  const saveTherapistNote = async () => {
    if (!anamnesisResponse) return;
    setSavingNote(true);
    const now = new Date().toISOString();
    await supabase
      .from('anamnesis_responses')
      .update({ therapist_notes: therapistNote.trim() || null, therapist_notes_updated_at: now })
      .eq('id', anamnesisResponse.id);
    setSavingNote(false);
    setEditingNote(false);
    setTherapistNoteUpdatedAt(now);
    showToast('Anteckning sparad', 'success');
  };

  const submitManualAnamnesis = async () => {
    setManualSubmitting(true);
    const { data: resp, error: respError } = await supabase
      .from('anamnesis_responses')
      .insert({ therapist_id: therapist.id, client_id: client.id, template_id: anamnesisTemplate.id, filled_by: 'therapist' })
      .select().single();

    if (respError) { showToast('Fel: ' + respError.message, 'error'); setManualSubmitting(false); return; }

    const answers = anamnesisQuestions.map(q => ({
      response_id: resp.id,
      question_id: q.id,
      answer_text: manualAnswers[q.id] || '',
    }));
    if (answers.length > 0) {
      await supabase.from('anamnesis_answers').insert(answers);
    }
    await fetchAnamnesis();
    setManualFillMode(false);
    setManualAnswers({});
    setManualSubmitting(false);
  };

  const startEditAnamnesis = () => {
    const preloaded = {};
    (anamnesisResponse?.answers || []).forEach(ans => {
      preloaded[ans.question_id] = ans.answer_text;
    });
    setEditAnswers(preloaded);
    setEditAnamnesisMode(true);
  };

  const updateAnamnesis = async () => {
    setEditSubmitting(true);
    await supabase.from('anamnesis_answers').delete().eq('response_id', anamnesisResponse.id);
    const answers = anamnesisQuestions.map(q => ({
      response_id: anamnesisResponse.id,
      question_id: q.id,
      answer_text: editAnswers[q.id] || '',
    }));
    if (answers.length > 0) {
      await supabase.from('anamnesis_answers').insert(answers);
    }
    await supabase
      .from('anamnesis_responses')
      .update({ filled_by: 'therapist', submitted_at: new Date().toISOString() })
      .eq('id', anamnesisResponse.id);
    await fetchAnamnesis();
    setEditAnamnesisMode(false);
    setEditAnswers({});
    setEditSubmitting(false);
    showToast('Anamnes sparad', 'success');
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedProgram(null);
    setSelectedProgramSource(null);
    if (tab === 'messages') setUnreadCount(0);
    if (tab !== 'messages') fetchMessageSummary();
    if (tab === 'programs') {
      fetchPrograms();
    }
    if (tab === 'anamnesis' && !anamnesisLoaded && !anamnesisLoading) {
      fetchAnamnesis();
    }
  };

  const saveClient = async () => {
    const { error } = await supabase
      .from('clients')
      .update({ name, email: email || null, phone: phone || null })
      .eq('id', client.id);

    if (error) {
      showToast('Fel: ' + error.message, 'error');
    } else {
      setEditing(false);
      client.name = name;
      client.email = email || null;
      client.phone = phone || null;
    }
  };

  // ─── Tema ────────────────────────────────────────────────────────────────────
  const theme = resolveTheme(accentColor);

  const TAB_DEFS = [
    { id: 'overview',  label: 'Översikt'    },
    { id: 'programs',  label: 'Program'     },
    { id: 'bookings',  label: 'Bokningar'   },
    { id: 'messages',  label: 'Meddelanden', badge: unreadCount },
    { id: 'notes',     label: 'Journal'     },
    { id: 'anamnesis', label: 'Anamnes'     },
  ];

  const clientSubtitle = [client.email, client.phone].filter(Boolean).join(' · ');

  return (
    <div>

      {/* ── Tillbaka ── */}
      <div style={{ marginBottom: '12px' }}>
        <button type="button" onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: NEUTRAL.muted, fontSize: '13px', fontWeight: '600', padding: 0, fontFamily: 'inherit' }}>← Alla kunder</button>
      </div>

      {/* ── Klienthuvud: mörkt band (T-12) ── */}
      {(() => {
        const todayStr = new Date().toISOString().split('T')[0];
        const visits = clientBookings.filter(b => b.status !== 'cancelled' && b.date < todayStr).length;
        const since = client.created_at ? new Date(client.created_at) : null;
        const MONTHS = ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december'];
        const sinceLabel = since ? `Kund sedan ${MONTHS[since.getMonth()]} ${since.getFullYear()}` : null;
        const ghost = { background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.22)', color: '#FFFFFF', padding: '9px 14px', borderRadius: theme.btn_radius, fontSize: '13px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' };
        return (
          <div className="fr-client-hero" style={{ position: 'relative', overflow: 'hidden', background: NEUTRAL.ink, borderRadius: theme.card_radius, padding: editing ? '26px 28px' : '26px 28px 64px', color: '#FFFFFF' }}>
            <div aria-hidden="true" style={{ position: 'absolute', top: '-150px', right: '-70px', width: '380px', height: '380px', borderRadius: '999px', background: theme.hero_glow, opacity: 0.30, filter: 'blur(40px)', pointerEvents: 'none' }} />
            <div className="fr-client-hero-inner" style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', minWidth: 0 }}>
                <Avatar theme={theme} name={client.name} size="lg" />
                <div style={{ minWidth: 0 }}>
                  <h1 style={{ margin: 0, fontSize: '26px', fontWeight: '800', letterSpacing: '-0.01em', lineHeight: 1.1, color: '#FFFFFF', fontFamily: 'var(--font-heading)' }}>{editing ? 'Redigera kund' : client.name}</h1>
                  {!editing && clientSubtitle && <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.72)', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{clientSubtitle}</div>}
                  {!editing && (sinceLabel || !loadingBookings) && (
                    <div style={{ fontSize: '12px', color: theme.accent_soft_bg, marginTop: '4px' }}>
                      {[sinceLabel, !loadingBookings ? `${visits} ${visits === 1 ? 'besök' : 'besök'}` : null].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </div>
              </div>
              {!editing && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                  <button type="button" onClick={() => setEditing(true)} style={ghost}>Redigera</button>
                  {onNewBooking && (
                    <button type="button" onClick={onNewBooking} style={{ background: theme.hero_btn_bg, border: `1px solid ${theme.hero_btn_bg}`, color: theme.hero_btn_text, padding: '9px 14px', borderRadius: theme.btn_radius, fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>+ Ny bokning</button>
                  )}
                </div>
              )}
            </div>
            {editing && (
              <div style={{ position: 'relative', marginTop: '18px', maxWidth: '520px' }}>
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ display: 'block', fontSize: '0.82em', fontWeight: '600', color: 'rgba(255,255,255,0.85)', marginBottom: '4px' }}>Namn</label>
                  <FormInput theme={theme} value={name} onChange={e => setName(e.target.value)} style={{ marginBottom: 0 }} />
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ display: 'block', fontSize: '0.82em', fontWeight: '600', color: 'rgba(255,255,255,0.85)', marginBottom: '4px' }}>E-post</label>
                  <FormInput theme={theme} value={email} onChange={e => setEmail(e.target.value)} style={{ marginBottom: 0 }} />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '0.82em', fontWeight: '600', color: 'rgba(255,255,255,0.85)', marginBottom: '4px' }}>Telefon</label>
                  <FormInput theme={theme} value={phone} onChange={e => setPhone(e.target.value)} style={{ marginBottom: 0 }} />
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button type="button" onClick={saveClient} style={{ background: '#FFFFFF', color: NEUTRAL.ink, border: '1px solid #FFFFFF', padding: '8px 14px', borderRadius: theme.btn_radius, fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}>Spara</button>
                  <button type="button" onClick={() => setEditing(false)} style={{ ...ghost, padding: '8px 14px' }}>Avbryt</button>
                  {onDeleteClient && (
                    <button type="button" onClick={onDeleteClient} style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: '#FCA5A5', padding: '8px 4px', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>Ta bort kund</button>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Nyckeltal (överlappar huvudet) ── */}
      {!editing && (() => {
        const todayStr = new Date().toISOString().split('T')[0];
        const nextBooking = clientBookings.find(b => b.date >= todayStr && b.status !== 'cancelled') || null;
        const firstActive = (compliance?.programStats || []).find(ps => !ps.endDate || ps.endDate > todayStr) || null;
        let compliancePct = null, done7 = 0, exp7 = 0;
        if (firstActive) {
          done7 = firstActive.last7.reduce((s, d) => s + d.count, 0);
          exp7  = firstActive.last7.reduce((s, d) => s + d.total, 0);
          if (exp7 > 0) compliancePct = Math.round((done7 / exp7) * 100);
        }
        const MONTHS_SHORT = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
        const DAYS_SHORT = ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör'];
        const fmtDay = (dateStr) => {
          if (dateStr === todayStr) return 'Idag';
          const d = new Date(dateStr + 'T12:00:00');
          return `${DAYS_SHORT[d.getDay()]} ${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
        };
        const pctColor = compliancePct === null ? NEUTRAL.ink : compliancePct >= 70 ? '#15803D' : compliancePct >= 35 ? STATUS_COLORS.warning : STATUS_COLORS.error;
        const filledAt = anamnesisResponse?.submitted_at ? new Date(anamnesisResponse.submitted_at) : null;
        const tiles = [
          {
            label: 'Nästa bokning',
            value: nextBooking ? `${fmtDay(nextBooking.date)} ${nextBooking.start_time?.slice(0, 5)}` : '–',
            sub: nextBooking ? (nextBooking.service?.title || 'Bokning') : 'Ingen kommande',
            accent: !!nextBooking,
            onClick: () => handleTabChange('bookings'),
          },
          {
            label: 'Aktivt program',
            value: firstActive ? firstActive.title : '–',
            sub: firstActive
              ? [firstActive.activeBlock, firstActive.currentWeek && firstActive.totalWeeks ? `vecka ${firstActive.currentWeek} av ${firstActive.totalWeeks}` : null].filter(Boolean).join(' · ') || 'Pågående'
              : 'Inget program tilldelat',
            onClick: () => handleTabChange('programs'),
          },
          {
            label: 'Följsamhet',
            value: compliancePct !== null ? `${compliancePct} %` : '–',
            sub: compliancePct !== null ? `${done7} av ${exp7} pass senaste veckan` : 'Ingen aktivitet att mäta',
            color: pctColor,
          },
          {
            label: 'Anamnes',
            value: !anamnesisTemplate ? '–' : anamnesisResponse ? 'Ifylld' : 'Saknas',
            dot: !anamnesisTemplate ? null : anamnesisResponse ? STATUS_COLORS.success : STATUS_COLORS.warning,
            sub: !anamnesisTemplate ? 'Ingen mall' : anamnesisResponse ? `${filledAt ? `${filledAt.getDate()} ${MONTHS_SHORT[filledAt.getMonth()]} · ` : ''}av ${anamnesisResponse.filled_by === 'therapist' ? 'dig' : 'kunden'}` : 'Fyll i →',
            onClick: () => handleTabChange('anamnesis'),
          },
        ];
        return (
          <div className="fr-client-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '12px', margin: '-56px 16px 16px', position: 'relative' }}>
            {tiles.map((t, i) => (
              <div
                key={i}
                onClick={t.onClick}
                style={{ background: theme.card_bg, border: `1px solid ${theme.card_border}`, borderRadius: theme.card_radius, boxShadow: theme.card_shadow, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0, cursor: t.onClick ? 'pointer' : 'default' }}
              >
                <div style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', color: NEUTRAL.muted }}>{t.label}</div>
                <div style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', fontWeight: '800', lineHeight: 1, color: t.color || (t.accent ? theme.accent_text : NEUTRAL.ink), display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {t.dot && <span style={{ width: '8px', height: '8px', borderRadius: '999px', background: t.dot, flexShrink: 0 }} />}
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.value}</span>
                </div>
                <div style={{ fontSize: '12px', color: NEUTRAL.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.sub}</div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* ── Tab-navigation ── */}
      <Tabs theme={theme} tabs={TAB_DEFS} activeTab={activeTab} onChange={handleTabChange} />

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── ÖVERSIKT ── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'overview' && (
        <div>
          {selectedProgram && selectedProgramSource === 'overview' ? (
            <AssignedProgramDetail
              cp={selectedProgram}
              onBack={() => { setSelectedProgram(null); setSelectedProgramSource(null); }}
              todayStr={new Date().toISOString().split('T')[0]}
              backLabel="← Översikt"
              theme={theme}
            />
          ) : (
            (() => {
              const todayStr = new Date().toISOString().split('T')[0];
              const MONTHS_SHORT = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
              const DAYS_SHORT = ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör'];
              const fmtShort = (dateStr) => { const d = new Date(dateStr + 'T12:00:00'); return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`; };
              const fmtDay = (dateStr) => { if (dateStr === todayStr) return 'Idag'; const d = new Date(dateStr + 'T12:00:00'); return `${DAYS_SHORT[d.getDay()]} ${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`; };
              const stats = compliance?.programStats || [];
              const activeStats = stats.filter(ps => !ps.endDate || ps.endDate > todayStr);
              const endedStats = stats.filter(ps => ps.endDate && ps.endDate <= todayStr);
              const upcoming = clientBookings.filter(b => b.date >= todayStr && b.status !== 'cancelled');
              const pastCount = clientBookings.filter(b => b.date < todayStr && b.status !== 'cancelled').length;
              const cardStyle = { background: theme.card_bg, border: `1px solid ${theme.card_border}`, borderRadius: theme.card_radius, boxShadow: theme.card_shadow, overflow: 'hidden' };
              const headStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '14px 20px', borderBottom: `1px solid ${theme.card_border}` };
              const h2Style = { margin: 0, fontSize: '16px', fontWeight: '700', color: NEUTRAL.ink, fontFamily: 'var(--font-heading)' };
              const link = { background: 'none', border: 'none', color: theme.accent_text, fontSize: '12px', fontWeight: '600', cursor: 'pointer', padding: 0, fontFamily: 'inherit', whiteSpace: 'nowrap' };
              const linkMuted = { ...link, color: NEUTRAL.muted, fontWeight: '500' };
              const emptyStyle = { margin: '12px', padding: '20px', textAlign: 'center', borderRadius: '10px', border: `1px dashed ${theme.card_border}`, fontSize: '12px', color: NEUTRAL.muted };
              const openProgram = (ps) => { if (ps.cpRef) { setSelectedProgram(ps.cpRef); setSelectedProgramSource('overview'); } };

              return (
                <div className="fr-client-grid" style={{ display: 'grid', gridTemplateColumns: '1.25fr 1fr', gap: '16px', alignItems: 'start' }}>

                  {/* Program */}
                  <div style={cardStyle}>
                    <div style={headStyle}>
                      <h2 style={h2Style}>Program</h2>
                      <button type="button" style={link} onClick={() => { handleTabChange('programs'); setShowAssignForm(true); }}>+ Tilldela program</button>
                    </div>
                    {loadingCompliance ? (
                      <div style={emptyStyle}>Laddar aktivitetsdata…</div>
                    ) : complianceError ? (
                      <div style={{ ...emptyStyle, color: STATUS_COLORS.error }}>{complianceError}</div>
                    ) : stats.length === 0 ? (
                      <div style={emptyStyle}>Inget program tilldelat ännu</div>
                    ) : (
                      <>
                        {activeStats.map((ps, i) => {
                          const last = i === activeStats.length - 1 && endedStats.length === 0;
                          const lastLog = ps.last7.slice().reverse().find(d => d.count > 0);
                          return (
                            <div key={ps.programId} style={{ padding: '14px 20px', borderBottom: last ? 'none' : `1px solid ${theme.card_border}`, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontSize: '14px', fontWeight: '600', color: NEUTRAL.ink }}>{ps.title}</div>
                                  <div style={{ fontSize: '12px', color: NEUTRAL.muted }}>
                                    {[ps.activeBlock, ps.currentWeek && ps.totalWeeks ? `vecka ${ps.currentWeek} av ${ps.totalWeeks}` : null, ps.frequency ? `${ps.frequency} pass/vecka` : null].filter(Boolean).join(' · ') || 'Pågående'}
                                  </div>
                                </div>
                                <button type="button" style={link} onClick={() => openProgram(ps)}>Öppna →</button>
                              </div>
                              <div className="fr-client-dots" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                <div style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.06em', textTransform: 'uppercase', color: NEUTRAL.muted, width: '96px', flexShrink: 0 }}>Senaste 7 dgr</div>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  {ps.last7.map((day) => {
                                    const isToday = day.date === todayStr;
                                    const full = day.total > 0 && day.count >= day.total;
                                    const some = day.count > 0 && !full;
                                    return (
                                      <div key={day.date} style={{ width: '30px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }} title={`${day.count} av ${day.total}`}>
                                        <span style={{ width: '22px', height: '22px', borderRadius: '999px', boxSizing: 'border-box', background: full ? theme.accent : some ? theme.accent_soft_bg : 'transparent', border: day.count > 0 ? 'none' : `1px dashed ${NEUTRAL.line}` }} />
                                        <span style={{ fontSize: '10px', color: isToday ? theme.accent_soft_text : NEUTRAL.muted, fontWeight: isToday ? '600' : '400' }}>{isToday ? 'Idag' : day.dayName.replace('.', '')}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                                <div style={{ marginLeft: 'auto', fontSize: '12px', color: NEUTRAL.muted }}>
                                  {lastLog ? <>Senast loggat <span style={{ color: NEUTRAL.ink }}>{lastLog.date === todayStr ? 'idag' : fmtShort(lastLog.date)}</span></> : 'Inget loggat senaste veckan'}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        {endedStats.map((ps, i) => (
                          <div key={ps.programId} style={{ padding: '12px 20px', borderBottom: i < endedStats.length - 1 ? `1px solid ${theme.card_border}` : 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', opacity: 0.6 }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: NEUTRAL.ink }}>{ps.title}</div>
                              <div style={{ fontSize: '12px', color: NEUTRAL.muted }}>Avslutat {fmtShort(ps.endDate)}{ps.totalWeeks ? ` · ${ps.totalWeeks} veckor` : ''} · {ps.uniqueDays} aktiva dagar</div>
                            </div>
                            <button type="button" style={link} onClick={() => openProgram(ps)}>Öppna →</button>
                          </div>
                        ))}
                      </>
                    )}
                  </div>

                  {/* Höger kolumn */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 }}>
                    <div style={cardStyle}>
                      <div style={headStyle}>
                        <h2 style={h2Style}>Bokningar</h2>
                        <span style={{ fontSize: '12px', color: NEUTRAL.muted }}>{upcoming.length} kommande</span>
                      </div>
                      {loadingBookings ? (
                        <div style={emptyStyle}>Laddar…</div>
                      ) : upcoming.length === 0 ? (
                        <div style={emptyStyle}>Ingen kommande bokning</div>
                      ) : upcoming.slice(0, 3).map((b, i) => {
                        const isToday = b.date === todayStr;
                        return (
                          <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 20px', borderBottom: `1px solid ${theme.card_border}`, background: isToday ? theme.accent_muted : 'transparent' }}>
                            <div style={{ width: '74px', flexShrink: 0, fontSize: '12px', fontWeight: isToday ? '600' : '400', color: isToday ? theme.accent_soft_text : NEUTRAL.muted }}>{fmtDay(b.date)}</div>
                            <div style={{ flex: 1, minWidth: 0, fontSize: '13px', color: NEUTRAL.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <span style={{ fontWeight: '600' }}>{b.start_time?.slice(0, 5)}</span>
                              {b.service?.title && <span style={{ color: NEUTRAL.muted }}> · {b.service.title}</span>}
                            </div>
                          </div>
                        );
                      })}
                      <div style={{ padding: '10px 20px', textAlign: 'center' }}>
                        <button type="button" style={linkMuted} onClick={() => handleTabChange('bookings')}>Alla bokningar{pastCount > 0 ? ` · ${pastCount} tidigare` : ''} →</button>
                      </div>
                    </div>

                    <div style={cardStyle}>
                      <div style={headStyle}>
                        <h2 style={h2Style}>Meddelanden</h2>
                        <button type="button" style={link} onClick={() => handleTabChange('messages')}>Öppna →</button>
                      </div>
                      {latestMessage ? (
                        <div style={{ padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {unreadCount > 0 && <span style={{ width: '8px', height: '8px', borderRadius: '999px', background: '#7C3AED', flexShrink: 0 }} />}
                            <span style={{ fontSize: '12px', color: NEUTRAL.muted }}>
                              {latestMessage.sender_role === 'client' ? (client.name || '').split(' ')[0] : 'Du'} · {new Date(latestMessage.created_at).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })} {new Date(latestMessage.created_at).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          <div style={{ fontSize: '13px', color: NEUTRAL.ink, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>”{latestMessage.content}”</div>
                        </div>
                      ) : (
                        <div style={emptyStyle}>Inga meddelanden ännu</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()
          )}
        </div>
      )}

      {activeTab === 'programs' && (() => {
        const todayStr = new Date().toISOString().split('T')[0];

        if (selectedProgram && selectedProgramSource !== 'overview') {
          return (
            <AssignedProgramDetail
              cp={selectedProgram}
              onBack={() => { setSelectedProgram(null); setSelectedProgramSource(null); }}
              todayStr={todayStr}
              theme={theme}
            />
          );
        }

        const now = new Date();
        const assignedIds = programs.map(cp => cp.program_id);
        const available = allPrograms.filter(p => !assignedIds.includes(p.id));
        const active = programs.filter(cp => !cp.end_date || cp.end_date > todayStr);
        const past = programs.filter(cp => cp.end_date && cp.end_date <= todayStr);

        const fmtDate = (d) => d ? new Date(d).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

        const renderProgram = (cp) => {
          const blocks = cp.program?.program_blocks || [];
          const isActive = !cp.end_date || cp.end_date > todayStr;

          let status = 'active';
          if (cp.end_date && cp.end_date <= todayStr) status = 'ended';
          else if (cp.start_date && cp.start_date > todayStr) status = 'upcoming';

          const statusBadgeVariant = status === 'active' ? 'success' : status === 'ended' ? 'neutral' : 'accent';

          const currentWeek = cp.start_date ? Math.max(1, Math.floor((now - new Date(cp.start_date)) / (1000 * 60 * 60 * 24 * 7)) + 1) : null;
          const totalWeeks = cp.start_date && cp.end_date ? Math.max(1, Math.ceil((new Date(cp.end_date) - new Date(cp.start_date)) / (1000 * 60 * 60 * 24 * 7))) : null;
          const showWeek = status === 'active' && currentWeek && totalWeeks && currentWeek <= totalWeeks;

          return (
            <Card
              key={cp.id}
              theme={theme}
              onClick={() => { setSelectedProgram(cp); setSelectedProgramSource('programs'); }}
              style={{ padding: '18px 22px', marginBottom: '10px' }}
            >
              {/* Title row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                <h3 style={{ margin: 0, fontSize: '0.95em', fontWeight: '700', color: status === 'ended' ? theme.text_secondary : theme.text_primary, lineHeight: 1.3 }}>
                  {cp.program?.title}
                </h3>
                {showWeek && (
                  <div style={{ marginLeft: '12px', flexShrink: 0 }}>
                    <Badge theme={theme} variant="accent">Vecka {currentWeek}/{totalWeeks}</Badge>
                  </div>
                )}
              </div>

              {/* Chip row: status + dates + frequency + affordance */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px', alignItems: 'center' }}>
                <Badge theme={theme} variant={statusBadgeVariant}>
                  {status === 'active' ? 'Aktiv' : status === 'ended' ? 'Avslutad' : 'Kommande'}
                </Badge>
                {cp.start_date && (
                  <span style={{ background: NEUTRAL.page, color: NEUTRAL.muted, border: `1px solid ${NEUTRAL.line}`, padding: '3px 10px', borderRadius: '10px', fontSize: '0.75em', fontWeight: '500' }}>
                    {fmtDate(cp.start_date)} – {cp.end_date ? fmtDate(cp.end_date) : 'pågående'}
                  </span>
                )}
                {cp.frequency_per_week && (
                  <span style={{ background: NEUTRAL.page, color: NEUTRAL.muted, border: `1px solid ${NEUTRAL.line}`, padding: '3px 10px', borderRadius: '10px', fontSize: '0.75em', fontWeight: '500' }}>
                    {cp.frequency_per_week}x/vecka
                  </span>
                )}
                <span style={{ marginLeft: 'auto', fontSize: '0.75em', color: theme.accent_text, fontWeight: '500', whiteSpace: 'nowrap' }}>
                  Visa program →
                </span>
              </div>

              {/* Description */}
              {cp.program?.description && (
                <p style={{ margin: '0 0 12px', color: theme.text_secondary, fontSize: '0.875em', lineHeight: '1.5' }}>
                  {cp.program.description}
                </p>
              )}

              {/* Blocks */}
              {blocks.length > 0 && (
                <div style={{ marginTop: '4px' }}>
                  {blocks.sort((a, b) => a.sort_order - b.sort_order).map((block) => (
                    <div key={block.id} style={{ padding: '10px 0', borderTop: `1px solid ${theme.card_border}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <span style={{ fontSize: '0.875em', fontWeight: '600', color: theme.text_primary }}>{block.title}</span>
                        <Badge theme={theme} variant="neutral">Vecka {block.start_week}–{block.end_week}</Badge>
                      </div>
                      {block.program_exercises?.length > 0 && (
                        <div style={{ paddingLeft: '4px' }}>
                          {block.program_exercises.sort((a, b) => a.sort_order - b.sort_order).map((pe, i) => (
                            <div key={pe.id} style={{ fontSize: '0.82em', color: theme.text_secondary, padding: '2px 0' }}>
                              {i + 1}. {pe.exercise?.title}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Action buttons */}
              <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: `1px solid ${theme.card_border}`, display: 'flex', gap: '8px' }}>
                {isActive && (
                  <Button
                    theme={theme}
                    variant="secondary"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); endAssignment(cp.id); }}
                  >
                    Avsluta
                  </Button>
                )}
                <Button
                  theme={theme}
                  variant="danger"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); unassignClient(cp.id); }}
                >
                  Ta bort
                </Button>
              </div>
            </Card>
          );
        };

        return (
          <div>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <p style={{ margin: 0, fontSize: '0.75em', fontWeight: '600', color: theme.text_secondary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {programs.length === 0 ? 'Program' : programs.length === 1 ? '1 program' : `${programs.length} program`}
              </p>
              {!showAssignForm && (
                <Button theme={theme} variant="secondary" size="sm" onClick={() => setShowAssignForm(true)}>
                  + Tilldela program
                </Button>
              )}
            </div>

            {/* Assign form */}
            {showAssignForm && (
              <div style={{ marginBottom: '20px' }}>
                <SectionCard theme={theme} title="Tilldela program">
                  {available.length === 0 ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: theme.text_secondary, fontSize: '0.875em' }}>Alla program är redan tilldelade denna kund.</span>
                      <Button theme={theme} variant="tertiary" size="sm" onClick={() => setShowAssignForm(false)}>Stäng</Button>
                    </div>
                  ) : (
                    <>
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', fontSize: '0.82em', fontWeight: '600', color: NEUTRAL.text, marginBottom: '4px' }}>Program</label>
                        <select
                          value={assignProgramId}
                          onChange={e => setAssignProgramId(e.target.value)}
                          style={{ width: '100%', padding: '9px 12px', borderRadius: '7px', border: `1px solid ${theme.card_border}`, fontSize: '0.9em', background: 'white', outline: 'none', boxSizing: 'border-box' }}
                        >
                          <option value="">Välj program...</option>
                          {available.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                        </select>
                      </div>
                      <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                        <FormInput
                          theme={theme}
                          label="Startdatum"
                          type="date"
                          value={assignStartDate}
                          onChange={e => {
                            setAssignStartDate(e.target.value);
                            if (e.target.value) {
                              const d = new Date(e.target.value);
                              d.setDate(d.getDate() + 56);
                              setAssignEndDate(d.toISOString().split('T')[0]);
                            }
                          }}
                          style={{ flex: 1, marginBottom: 0 }}
                        />
                        <FormInput
                          theme={theme}
                          label="Slutdatum"
                          type="date"
                          value={assignEndDate}
                          onChange={e => setAssignEndDate(e.target.value)}
                          style={{ flex: 1, marginBottom: 0 }}
                        />
                        <FormInput
                          theme={theme}
                          label="Pass/vecka"
                          type="number"
                          min="1"
                          max="7"
                          value={assignFrequency}
                          onChange={e => setAssignFrequency(e.target.value)}
                          style={{ flex: '0 0 90px', marginBottom: 0 }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Button
                          theme={theme}
                          variant="primary"
                          loading={assigning}
                          disabled={!assignProgramId || assigning}
                          onClick={handleAssign}
                        >
                          Tilldela program
                        </Button>
                        <Button
                          theme={theme}
                          variant="tertiary"
                          onClick={() => { setShowAssignForm(false); setAssignProgramId(''); }}
                        >
                          Avbryt
                        </Button>
                      </div>
                    </>
                  )}
                </SectionCard>
              </div>
            )}

            {/* Program list */}
            {loadingPrograms ? (
              <p style={{ color: theme.text_secondary }}>Laddar...</p>
            ) : programs.length === 0 ? (
              <EmptyState
                theme={theme}
                title="Inga program tilldelade ännu"
                message="Tilldela ett program från biblioteket för att börja följa kundens träning och framsteg."
                cta={!showAssignForm && (
                  <Button theme={theme} variant="secondary" size="sm" onClick={() => setShowAssignForm(true)}>
                    + Tilldela program
                  </Button>
                )}
              />
            ) : (
              <div>
                {active.length > 0 && (
                  <div>
                    {past.length > 0 && (
                      <p style={{ margin: '0 0 10px', fontSize: '0.75em', fontWeight: '600', color: theme.text_secondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Aktiva</p>
                    )}
                    {active.map(renderProgram)}
                  </div>
                )}
                {past.length > 0 && (
                  <div style={{ marginTop: active.length > 0 ? '20px' : '0' }}>
                    <p style={{ margin: '0 0 10px', fontSize: '0.75em', fontWeight: '600', color: theme.text_secondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Avslutade</p>
                    {past.map(renderProgram)}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {activeTab === 'bookings' && (() => {
        const todayStr = new Date().toISOString().split('T')[0];
        const fmtBookingDate = (dateStr) => {
          const [y, m, d] = dateStr.split('-').map(Number);
          return new Date(y, m - 1, d).toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' });
        };
        const fmtPrice = (b) => {
          const p = b.campaign_active_snapshot ? b.campaign_price_snapshot : b.price_snapshot;
          if (p == null) return null;
          return `${p} ${currencyLabel(b.currency_snapshot)}`;
        };

        const upcoming = clientBookings.filter(b => b.date >= todayStr && b.status !== 'cancelled');
        const history = clientBookings
          .filter(b => b.date < todayStr || b.status === 'cancelled')
          .sort((a, b) => b.date.localeCompare(a.date) || (b.start_time || '').localeCompare(a.start_time || ''));
        const nextBooking = upcoming[0] || null;
        const moreUpcoming = upcoming.slice(1);

        if (loadingBookings) {
          return <p style={{ color: theme.text_secondary }}>Laddar bokningar...</p>;
        }

        return (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <SectionCard theme={theme} title="Nästa bokning">
                {nextBooking ? (
                  <ListItem
                    theme={theme}
                    title={fmtBookingDate(nextBooking.date)}
                    meta={[
                      nextBooking.start_time?.slice(0, 5) + (nextBooking.end_time ? `–${nextBooking.end_time.slice(0, 5)}` : ''),
                      nextBooking.service?.title,
                      fmtPrice(nextBooking),
                    ].filter(Boolean).join(' · ')}
                  />
                ) : (
                  <p style={{ color: theme.text_secondary, fontSize: '0.875em', margin: 0 }}>Ingen kommande bokning</p>
                )}
              </SectionCard>
            </div>

            {moreUpcoming.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <SectionCard theme={theme} title="Kommande bokningar">
                  {moreUpcoming.map(b => (
                    <ListItem
                      key={b.id}
                      theme={theme}
                      title={fmtBookingDate(b.date)}
                      meta={[
                        b.start_time?.slice(0, 5) + (b.end_time ? `–${b.end_time.slice(0, 5)}` : ''),
                        b.service?.title,
                        fmtPrice(b),
                      ].filter(Boolean).join(' · ')}
                    />
                  ))}
                </SectionCard>
              </div>
            )}

            <SectionCard theme={theme} title="Bokningshistorik">
              {history.length === 0 ? (
                <p style={{ color: theme.text_secondary, fontSize: '0.875em', margin: 0 }}>Ingen historik</p>
              ) : (
                <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
                  {history.map(b => (
                    <ListItem
                      key={b.id}
                      theme={theme}
                      title={fmtBookingDate(b.date)}
                      meta={[
                        b.start_time?.slice(0, 5) + (b.end_time ? `–${b.end_time.slice(0, 5)}` : ''),
                        b.service?.title,
                        fmtPrice(b),
                      ].filter(Boolean).join(' · ')}
                      right={b.status === 'cancelled' ? <Badge theme={theme} variant="error">Avbokad</Badge> : undefined}
                    />
                  ))}
                </div>
              )}
            </SectionCard>
          </div>
        );
      })()}

      {activeTab === 'messages' && (
        <ClientMessages therapist={therapist} client={client} theme={theme} />
      )}

      {activeTab === 'notes' && (
        <ClientNotes therapist={therapist} client={client} theme={theme} />
      )}

      {activeTab === 'anamnesis' && (
        <SectionCard
          theme={theme}
          title="Anamnes"
          meta={anamnesisResponse && !editAnamnesisMode ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><span style={{ width: '7px', height: '7px', borderRadius: '999px', background: STATUS_COLORS.success }} />ifylld {new Date(anamnesisResponse.submitted_at).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })} av {anamnesisResponse.filled_by === 'therapist' ? 'dig' : 'kunden'}</span>
              <button type="button" onClick={startEditAnamnesis} style={{ background: 'transparent', border: `1px solid ${theme.card_border}`, color: NEUTRAL.text, padding: '6px 12px', borderRadius: theme.btn_radius, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}>Redigera svar</button>
            </span>
          ) : anamnesisTemplate && !anamnesisResponse ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><span style={{ width: '7px', height: '7px', borderRadius: '999px', background: STATUS_COLORS.warning }} />ej besvarad</span>
          ) : undefined}
          flush={!!anamnesisResponse && !editAnamnesisMode}
        >
          {anamnesisLoading ? (
            <p style={{ color: theme.text_secondary }}>Laddar...</p>
          ) : anamnesisError ? (
            <p style={{ color: STATUS_COLORS.error, fontSize: '0.875em' }}>{anamnesisError}</p>
          ) : !anamnesisTemplate ? (
            <EmptyState
              theme={theme}
              title="Ingen anamnesblankett uppsatt"
              message="Gå till Bokningar → Anamnes för att skapa en blankett för dina kunder."
            />
          ) : !anamnesisResponse ? (
            <div>
              {!manualFillMode ? (
                <EmptyState
                  theme={theme}
                  title="Anamnes ej ifylld"
                  message="Kunden har inte fyllt i anamnesen digitalt."
                  cta={
                    <Button theme={theme} variant="secondary" size="sm" onClick={() => { setManualFillMode(true); setManualAnswers({}); }}>
                      Fyll i manuellt
                    </Button>
                  }
                />
              ) : (
                <div>
                  <p style={{ margin: '0 0 14px', color: theme.text_secondary, fontSize: '0.82em' }}>Fyll i åt kunden — sparas i kundprofilen</p>
                  {anamnesisTemplate.intro_text && (
                    <p style={{ margin: '0 0 18px', color: theme.text_secondary, fontSize: '0.875em', fontStyle: 'italic', lineHeight: '1.5' }}>
                      {anamnesisTemplate.intro_text}
                    </p>
                  )}
                  {anamnesisQuestions.map((q, index) => (
                    <div key={q.id} style={{ marginBottom: '14px' }}>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '7px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.875em', fontWeight: '600', color: NEUTRAL.text }}>{index + 1}. {q.question_text}</span>
                        {q.is_required && <Badge theme={theme} variant="error">Obligatorisk</Badge>}
                      </div>
                      {q.question_type === 'yes_no' ? (
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {['Ja', 'Nej'].map(opt => (
                            <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', padding: '7px 16px', borderRadius: '7px', border: `1px solid ${manualAnswers[q.id] === opt ? theme.accent : theme.card_border}`, background: manualAnswers[q.id] === opt ? theme.accent_soft_bg : 'white', fontSize: '0.875em', fontWeight: '500', color: manualAnswers[q.id] === opt ? theme.accent_soft_text : NEUTRAL.text }}>
                              <input type="radio" name={`mq_${q.id}`} value={opt} checked={manualAnswers[q.id] === opt} onChange={() => setManualAnswers(prev => ({ ...prev, [q.id]: opt }))} style={{ display: 'none' }} />
                              {opt}
                            </label>
                          ))}
                        </div>
                      ) : q.question_type === 'textarea' ? (
                        <textarea
                          value={manualAnswers[q.id] || ''}
                          onChange={(e) => setManualAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                          placeholder="Skriv svar..."
                          rows={3}
                          style={{ width: '100%', padding: '9px 12px', borderRadius: '7px', border: `1px solid ${theme.card_border}`, fontSize: '0.875em', outline: 'none', resize: 'vertical', boxSizing: 'border-box', lineHeight: '1.5', background: 'white' }}
                        />
                      ) : (
                        <FormInput
                          theme={theme}
                          type="text"
                          value={manualAnswers[q.id] || ''}
                          onChange={(e) => setManualAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                          placeholder="Skriv svar..."
                          style={{ marginBottom: 0 }}
                        />
                      )}
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                    <Button theme={theme} variant="primary" loading={manualSubmitting} disabled={manualSubmitting} onClick={submitManualAnamnesis}>
                      Spara anamnes
                    </Button>
                    <Button theme={theme} variant="tertiary" onClick={() => setManualFillMode(false)}>
                      Avbryt
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              {editAnamnesisMode ? (
                <div>
                  <p style={{ margin: '0 0 14px', color: theme.text_secondary, fontSize: '0.82em' }}>Ändringarna sparas och ersätter befintliga svar</p>
                  {anamnesisQuestions.map((q, index) => (
                    <div key={q.id} style={{ marginBottom: '14px' }}>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '7px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.875em', fontWeight: '600', color: NEUTRAL.text }}>{index + 1}. {q.question_text}</span>
                        {q.is_required && <Badge theme={theme} variant="error">Obligatorisk</Badge>}
                      </div>
                      {q.question_type === 'yes_no' ? (
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {['Ja', 'Nej'].map(opt => (
                            <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', padding: '7px 16px', borderRadius: '7px', border: `1px solid ${editAnswers[q.id] === opt ? theme.accent : theme.card_border}`, background: editAnswers[q.id] === opt ? theme.accent_soft_bg : 'white', fontSize: '0.875em', fontWeight: '500', color: editAnswers[q.id] === opt ? theme.accent_soft_text : NEUTRAL.text }}>
                              <input type="radio" name={`eq_${q.id}`} value={opt} checked={editAnswers[q.id] === opt} onChange={() => setEditAnswers(prev => ({ ...prev, [q.id]: opt }))} style={{ display: 'none' }} />
                              {opt}
                            </label>
                          ))}
                        </div>
                      ) : q.question_type === 'textarea' ? (
                        <textarea
                          value={editAnswers[q.id] || ''}
                          onChange={(e) => setEditAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                          rows={3}
                          style={{ width: '100%', padding: '9px 12px', borderRadius: '7px', border: `1px solid ${theme.card_border}`, fontSize: '0.875em', outline: 'none', resize: 'vertical', boxSizing: 'border-box', lineHeight: '1.5', background: 'white' }}
                        />
                      ) : (
                        <FormInput
                          theme={theme}
                          type="text"
                          value={editAnswers[q.id] || ''}
                          onChange={(e) => setEditAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                          style={{ marginBottom: 0 }}
                        />
                      )}
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                    <Button theme={theme} variant="primary" loading={editSubmitting} disabled={editSubmitting} onClick={updateAnamnesis}>
                      Spara ändringar
                    </Button>
                    <Button theme={theme} variant="tertiary" onClick={() => setEditAnamnesisMode(false)}>
                      Avbryt
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  {(anamnesisResponse.answers || [])
                    .sort((a, b) => (a.question?.sort_order ?? 0) - (b.question?.sort_order ?? 0))
                    .map((ans) => (
                      <div key={ans.id} style={{ padding: '12px 20px', borderBottom: `1px solid ${theme.card_border}` }}>
                        <div style={{ fontSize: '12px', color: NEUTRAL.muted, marginBottom: '3px' }}>{ans.question?.question_text}</div>
                        <div style={{ fontSize: '13px', color: ans.answer_text ? NEUTRAL.ink : NEUTRAL.faint, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{ans.answer_text || 'Inget svar angivet'}</div>
                      </div>
                    ))}
                  <div style={{ padding: '12px 20px', background: theme.accent_muted }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <span style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.06em', textTransform: 'uppercase', color: theme.accent_soft_text }}>Din bedömning <span style={{ fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}>· visas inte för kunden</span></span>
                      {!editingNote && (
                        <button type="button" onClick={() => setEditingNote(true)} style={{ background: 'none', border: 'none', color: theme.accent_text, fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>{therapistNote ? 'Ändra' : 'Lägg till'}</button>
                      )}
                    </div>
                    {editingNote ? (
                      <div>
                        <textarea
                          value={therapistNote}
                          onChange={(e) => setTherapistNote(e.target.value)}
                          placeholder="Anteckningar från mötet eller annat relevant..."
                          rows={4}
                          autoFocus
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${theme.card_border}`, fontSize: '0.875em', outline: 'none', resize: 'vertical', boxSizing: 'border-box', lineHeight: '1.6', background: '#fff' }}
                        />
                        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                          <Button theme={theme} variant="primary" size="sm" loading={savingNote} disabled={savingNote} onClick={saveTherapistNote}>Spara</Button>
                          <Button theme={theme} variant="tertiary" size="sm" onClick={() => { setEditingNote(false); setTherapistNote(anamnesisResponse.therapist_notes || ''); }}>Avbryt</Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p style={{ margin: 0, color: therapistNote ? theme.text_primary : theme.text_secondary, fontSize: '0.9em', lineHeight: '1.6', fontStyle: therapistNote ? 'normal' : 'italic' }}>
                          {therapistNote || 'Inga anteckningar ännu.'}
                        </p>
                        {therapistNoteUpdatedAt && (
                          <p style={{ margin: '8px 0 0', fontSize: '0.75em', color: theme.text_secondary }}>
                            Senast ändrad: {new Date(therapistNoteUpdatedAt).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' })}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </SectionCard>
      )}
    </div>
  );
}

function ClientMessages({ therapist, client, theme }) {
  const t = theme || resolveTheme(KLINIO_DEFAULT_ACCENT);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => fetchMessages(true), 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  async function fetchMessages(silent = false) {
    if (!silent) setLoading(true);
    try {
      const { data } = await supabase
        .from('messages')
        .select('*')
        .eq('therapist_id', therapist.id)
        .eq('client_id', client.id)
        .order('created_at', { ascending: true });
      setMessages(data || []);
    } finally {
      if (!silent) setLoading(false);
    }
    await supabase
      .from('messages')
      .update({ read: true })
      .eq('therapist_id', therapist.id)
      .eq('client_id', client.id)
      .eq('sender_role', 'client')
      .eq('read', false);
  }

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const { data: inserted, error } = await supabase.from('messages').insert({
      therapist_id: therapist.id,
      client_id: client.id,
      sender_role: 'therapist',
      content: newMessage.trim(),
    }).select('id').single();

    if (error) { showToast('Fel vid skickande av meddelande.', 'error'); return; }
    setNewMessage('');
    fetchMessages();
    if (inserted?.id) {
      supabase.functions.invoke('send-message-notification', {
        body: { message_id: inserted.id, app_url: window.location.origin },
      }).then(({ error: notifErr }) => {
        if (notifErr) console.error('[msg-notify] Fel från Edge Function:', notifErr.message, notifErr);
      }).catch(err => console.error('[msg-notify] Nätverksfel:', err));
    } else {
      console.warn('[msg-notify] inserted?.id saknas, invoke hoppas över. inserted:', inserted);
    }
  };

  // T-14: samma bubblor som kundens vy. Datumavdelare i stället för tidsstämpel per meddelande,
  // klockslag under bubblan, "Läst" under senaste egna lästa. Hämtning/skick/notis oförändrade.
  const firstName = (client?.name || '').trim().split(/\s+/)[0] || 'kunden';
  const accentText = onAccent(t.accent);
  const lastReadOwn = [...messages].reverse().find(m => m.sender_role === 'therapist' && m.read);
  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(e); }
  };

  return (
    <div style={{ background: NEUTRAL.card, border: `1px solid ${t.card_border}`, borderRadius: '14px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(10,7,6,0.04)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px', padding: '14px 18px', borderBottom: `1px solid ${t.card_border}` }}>
        <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: NEUTRAL.ink, fontFamily: 'var(--font-heading)' }}>Meddelanden</h2>
        <span style={{ fontSize: '12px', color: NEUTRAL.muted }}>Sparas på {firstName} – inte i din telefon</span>
      </div>
      <div ref={scrollRef} style={{ height: 'min(440px, 55vh)', overflowY: 'auto', padding: '16px', background: NEUTRAL.page, display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {loading ? (
          <div style={{ margin: 'auto', color: NEUTRAL.muted, fontSize: '13px' }}>Laddar…</div>
        ) : messages.length === 0 ? (
          <div style={{ margin: 'auto', textAlign: 'center', maxWidth: '300px' }}>
            <div style={{ fontSize: '14px', fontWeight: '700', color: NEUTRAL.ink }}>Inga meddelanden ännu</div>
            <div style={{ fontSize: '13px', color: NEUTRAL.muted, marginTop: '4px', lineHeight: 1.55 }}>Skriv det första nedan. {firstName} ser det i appen och får ett mejl.</div>
          </div>
        ) : messages.map((msg, i) => {
          const mine = msg.sender_role === 'therapist';
          const prev = messages[i - 1];
          const showDay = !prev || msgDayKey(prev.created_at) !== msgDayKey(msg.created_at);
          const time = new Date(msg.created_at).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
          return (
            <React.Fragment key={msg.id}>
              {showDay && <div style={{ textAlign: 'center', fontSize: '11px', color: NEUTRAL.faint, margin: '8px 0 2px' }}>{msgDayLabel(msg.created_at)}</div>}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start' }}>
                <div style={{ padding: '10px 14px', maxWidth: '72%', fontSize: '14px', lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: mine ? t.accent : NEUTRAL.card, color: mine ? accentText : NEUTRAL.ink, border: mine ? 'none' : `1px solid ${NEUTRAL.line}`, borderRadius: mine ? '14px 14px 4px 14px' : '14px 14px 14px 4px' }}>
                  {msg.content}
                </div>
                <div style={{ fontSize: '11px', color: NEUTRAL.faint, marginTop: '3px', padding: '0 4px' }}>
                  {time}{mine && lastReadOwn && lastReadOwn.id === msg.id ? ' · Läst' : ''}
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
      <form onSubmit={sendMessage} style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', padding: '12px 14px 6px', borderTop: `1px solid ${t.card_border}`, background: NEUTRAL.card }}>
        <label htmlFor="therapist-msg" style={{ position: 'absolute', left: '-9999px' }}>Skriv meddelande</label>
        <textarea
          id="therapist-msg"
          rows={1}
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={`Skriv till ${firstName}…`}
          style={{ flex: 1, minWidth: 0, minHeight: '44px', maxHeight: '140px', padding: '11px 14px', borderRadius: '12px', border: `1px solid ${NEUTRAL.line}`, fontSize: '14px', lineHeight: 1.45, outline: 'none', background: NEUTRAL.card, fontFamily: 'inherit', color: NEUTRAL.ink, resize: 'vertical', boxSizing: 'border-box' }}
        />
        <button type="submit" aria-label="Skicka" disabled={!newMessage.trim()} style={{ width: '44px', height: '44px', borderRadius: '12px', background: t.accent, border: 'none', color: accentText, fontSize: '18px', cursor: newMessage.trim() ? 'pointer' : 'default', opacity: newMessage.trim() ? 1 : 0.5, flexShrink: 0, fontFamily: 'inherit' }}>↑</button>
      </form>
      <div style={{ padding: '0 16px 12px', background: NEUTRAL.card, fontSize: '11px', color: NEUTRAL.faint }}>{firstName} får ett mejl om att hen har ett nytt meddelande. Enter skickar, Shift+Enter ger ny rad.</div>
    </div>
  );
}

const MSG_MONTHS = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const MSG_WEEKDAYS = ['Söndag', 'Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag'];
function msgDayKey(iso) { const d = new Date(iso); return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`; }
function msgDayLabel(iso) {
  const d = new Date(iso);
  const today = new Date(); const y = new Date(); y.setDate(y.getDate() - 1);
  if (msgDayKey(d) === msgDayKey(today)) return 'Idag';
  if (msgDayKey(d) === msgDayKey(y)) return 'Igår';
  const label = `${MSG_WEEKDAYS[d.getDay()]} ${d.getDate()} ${MSG_MONTHS[d.getMonth()]}`;
  return d.getFullYear() === today.getFullYear() ? label : `${label} ${d.getFullYear()}`;
}

function ClientNotes({ therapist, client, theme }) {
  const t = theme || resolveTheme(KLINIO_DEFAULT_ACCENT);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [editingNote, setEditingNote] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotes();
  }, []);

  async function fetchNotes() {
    setLoading(true);
    const { data } = await supabase
      .from('notes')
      .select('*')
      .eq('therapist_id', therapist.id)
      .eq('client_id', client.id)
      .order('created_at', { ascending: false });
    setNotes(data || []);
    setLoading(false);
  }

  const addNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    const { error } = await supabase.from('notes').insert({ therapist_id: therapist.id, client_id: client.id, content: newNote.trim() });
    if (error) showToast('Fel: ' + error.message, 'error');
    else { setNewNote(''); fetchNotes(); }
  };

  const saveEdit = async (noteId) => {
    if (!editContent.trim()) return;
    const { error } = await supabase.from('notes').update({ content: editContent.trim(), updated_at: new Date().toISOString() }).eq('id', noteId);
    if (error) showToast('Fel: ' + error.message, 'error');
    else { setEditingNote(null); setEditContent(''); fetchNotes(); }
  };

  const deleteNote = (noteId) => {
    showConfirm({
      title: 'Ta bort anteckning',
      description: 'Anteckningen tas bort permanent.',
      onConfirm: async () => {
        await supabase.from('notes').delete().eq('id', noteId);
        fetchNotes();
      },
    });
  };

  const MONTHS_SHORT = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
  const fmt = (iso) => { const d = new Date(iso); return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}${d.getFullYear() !== new Date().getFullYear() ? ` ${d.getFullYear()}` : ''}`; };
  const link = { background: 'none', border: 'none', color: NEUTRAL.muted, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit', padding: 0, whiteSpace: 'nowrap' };

  return (
    <SectionCard theme={t} title="Journal" meta={notes.length > 0 ? `${notes.length} ${notes.length === 1 ? 'anteckning' : 'anteckningar'} · bara synliga för dig` : 'bara synlig för dig'} flush>
      <form onSubmit={addNote} style={{ padding: '14px 20px', borderBottom: `1px solid ${t.card_border}`, display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <label htmlFor="new-note" style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.06em', textTransform: 'uppercase', color: NEUTRAL.muted }}>Ny anteckning</label>
        <textarea
          id="new-note"
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Skriv en journalanteckning…"
          rows={2}
          style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: `1px solid ${t.card_border}`, resize: 'vertical', boxSizing: 'border-box', fontSize: '13px', outline: 'none', lineHeight: '1.5', background: t.card_bg, fontFamily: 'inherit', color: NEUTRAL.ink }}
        />
        <div><Button theme={t} variant="primary" size="sm" type="submit" disabled={!newNote.trim()}>Spara anteckning</Button></div>
      </form>
      {loading ? (
        <div style={{ padding: '16px 20px', fontSize: '12px', color: NEUTRAL.muted }}>Laddar…</div>
      ) : notes.length === 0 ? (
        <div style={{ margin: '12px', padding: '20px', textAlign: 'center', borderRadius: '10px', border: `1px dashed ${t.card_border}`, fontSize: '12px', color: NEUTRAL.muted }}>Inga anteckningar ännu</div>
      ) : notes.map((note, i) => (
        <div key={note.id} style={{ padding: '12px 20px', borderBottom: i < notes.length - 1 ? `1px solid ${t.card_border}` : 'none', display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
          <div style={{ width: '64px', flexShrink: 0, fontSize: '12px', color: NEUTRAL.muted, paddingTop: '2px' }}>{fmt(note.created_at)}</div>
          {editingNote === note.id ? (
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} rows={3} style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: `1px solid ${t.card_border}`, resize: 'vertical', boxSizing: 'border-box', fontSize: '13px', outline: 'none', background: t.card_bg, fontFamily: 'inherit', color: NEUTRAL.ink }} />
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button theme={t} variant="primary" size="sm" onClick={() => saveEdit(note.id)}>Spara</Button>
                <Button theme={t} variant="tertiary" size="sm" onClick={() => setEditingNote(null)}>Avbryt</Button>
              </div>
            </div>
          ) : (
            <>
              <div style={{ flex: 1, minWidth: 0, fontSize: '13px', color: NEUTRAL.ink, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{note.content}</div>
              <div style={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
                <button type="button" onClick={() => { setEditingNote(note.id); setEditContent(note.content); }} style={link}>Ändra</button>
                <button type="button" onClick={() => deleteNote(note.id)} style={{ ...link, color: STATUS_COLORS.error }}>Ta bort</button>
              </div>
            </>
          )}
        </div>
      ))}
    </SectionCard>
  );
}

function AssignedProgramDetail({ cp, onBack, todayStr, backLabel = '← Programlista', theme }) {
  const program = cp.program;
  const blocks = (program?.program_blocks || []).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  let status = 'active';
  if (cp.end_date && cp.end_date < todayStr) status = 'ended';
  else if (cp.start_date && cp.start_date > todayStr) status = 'upcoming';

  const statusConfig = {
    active:   { label: 'Aktiv',    bg: STATUS_COLORS.success_bg, color: STATUS_COLORS.success, dot: STATUS_COLORS.success },
    ended:    { label: 'Avslutad', bg: NEUTRAL.page, color: NEUTRAL.muted, dot: null },
    upcoming: { label: 'Kommande', bg: STATUS_COLORS.info_bg, color: STATUS_COLORS.info, dot: STATUS_COLORS.info },
  };
  const sc = statusConfig[status];

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

  const fmtExMeta = (pe) => {
    const parts = [];
    if (pe.sets) parts.push(`${pe.sets} set`);
    if (pe.reps) parts.push(`${pe.reps} reps`);
    if (pe.duration_seconds) parts.push(`${Math.round(pe.duration_seconds / 60)} min`);
    return parts.join(' · ');
  };

  return (
    <div>
      {/* Back */}
      <button
        onClick={onBack}
        style={{ background: 'none', border: 'none', color: NEUTRAL.muted, cursor: 'pointer', marginBottom: '20px', padding: '4px 0', fontSize: '0.82em', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '5px' }}
      >
        {backLabel}
      </button>

      {/* Header card */}
      <div style={{ border: `1px solid ${NEUTRAL.line}`, borderRadius: '14px', padding: '22px 26px', marginBottom: '20px', background: 'white', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
          <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: '700', color: NEUTRAL.ink }}>
            {program?.title || '—'}
          </h3>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: sc.bg, color: sc.color, padding: '4px 12px', borderRadius: '10px', fontSize: '0.78em', fontWeight: '600', whiteSpace: 'nowrap', flexShrink: 0 }}>
            {sc.dot && <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: sc.dot, display: 'inline-block' }} />}
            {sc.label}
          </span>
        </div>

        {/* Meta row */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: program?.description ? '12px' : '0' }}>
          {cp.start_date && (
            <span style={{ background: NEUTRAL.page, color: NEUTRAL.muted, border: `1px solid ${NEUTRAL.line}`, padding: '3px 10px', borderRadius: '10px', fontSize: '0.75em', fontWeight: '500' }}>
              {fmtDate(cp.start_date)} – {cp.end_date ? fmtDate(cp.end_date) : 'pågående'}
            </span>
          )}
          {cp.frequency_per_week && (
            <span style={{ background: NEUTRAL.page, color: NEUTRAL.muted, border: `1px solid ${NEUTRAL.line}`, padding: '3px 10px', borderRadius: '10px', fontSize: '0.75em', fontWeight: '500' }}>
              {cp.frequency_per_week}x/vecka
            </span>
          )}
        </div>

        {program?.description && (
          <p style={{ margin: 0, color: NEUTRAL.muted, fontSize: '0.875em', lineHeight: '1.55' }}>
            {program.description}
          </p>
        )}
      </div>

      {/* Blocks */}
      <p style={{ margin: '0 0 12px', fontSize: '0.75em', fontWeight: '600', color: NEUTRAL.faint, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        Block &amp; övningar
      </p>

      {blocks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '36px 24px', border: `1px dashed ${NEUTRAL.line}`, borderRadius: '12px', background: NEUTRAL.page }}>
          <p style={{ margin: 0, color: NEUTRAL.faint, fontSize: '0.875em' }}>Inga block tillagda i detta program.</p>
        </div>
      ) : (
        blocks.map((block) => {
          const exercises = (block.program_exercises || []).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
          return (
            <div key={block.id} style={{ border: `1px solid ${NEUTRAL.line}`, borderRadius: '12px', padding: '16px 20px', marginBottom: '10px', background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
              {/* Block header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: exercises.length > 0 ? '12px' : '0' }}>
                <span style={{ fontSize: '0.9em', fontWeight: '700', color: NEUTRAL.ink }}>{block.title}</span>
                {(block.start_week || block.end_week) && (
                  <span style={{ background: NEUTRAL.page, color: NEUTRAL.muted, padding: '2px 9px', borderRadius: '10px', fontSize: '0.73em', fontWeight: '500' }}>
                    Vecka {block.start_week}–{block.end_week}
                  </span>
                )}
              </div>

              {exercises.length === 0 ? (
                <p style={{ margin: 0, color: NEUTRAL.faint, fontSize: '0.82em', fontStyle: 'italic' }}>Inga övningar i detta block.</p>
              ) : (
                exercises.map((pe, i) => {
                  const meta = fmtExMeta(pe);
                  return (
                    <div key={pe.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderTop: i === 0 ? 'none' : `1px solid ${NEUTRAL.page}` }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                        <span style={{ fontSize: '0.78em', color: NEUTRAL.faint, fontWeight: '600', flexShrink: 0 }}>{i + 1}.</span>
                        <span style={{ fontSize: '0.875em', color: NEUTRAL.text, fontWeight: '500' }}>{pe.exercise?.title || '—'}</span>
                      </div>
                      {meta && (
                        <span style={{ fontSize: '0.75em', color: NEUTRAL.muted, background: NEUTRAL.page, border: `1px solid ${NEUTRAL.line}`, padding: '2px 8px', borderRadius: '8px', whiteSpace: 'nowrap', marginLeft: '10px' }}>
                          {meta}
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

export default ClientProfile;
